/**
 * custom-allergen-filter.js
 *
 * 概要:
 * - アレルギーフィルター: Shopifyデフォルトのフィルターを使わず、独自のJS制御で実装
 *   （URLパラメーター `exclude_allergens` で管理し、該当商品をCSS表示/非表示で制御。除外方式）
 * - カテゴリフィルター: カテゴリ(custom.product_type metafield)はSearch & Discoveryアプリに
 *   未登録のためShopifyデフォルトのフィルターが使えず、アレルギーと同じ独自JS制御で実装
 *   （URLパラメーター `category` で管理し、該当商品をCSS表示/非表示で制御。選択したカテゴリのみ表示する包含方式）
 * - アレルギー除外とカテゴリ絞り込みは併用可能（AND条件）
 * - ページネーションなし（全商品を1ページに表示する前提）
 */

document.addEventListener("DOMContentLoaded", () => {
  const PARAM_KEY = "exclude_allergens";
  const CATEGORY_PARAM_KEY = "category";

  const selectedAllergens = new Set();
  const selectedCategories = new Set();

  // MutationObserver発火後の再初期化で重複登録を防ぐためWeakSetで管理
  // （data-allergen-init属性をDOMに書き込むとDawnのキャッシュに残るためWeakSetを使用）
  const initializedLabels = new WeakSet();
  const initializedCategoryLabels = new WeakSet();

  // ページロード時にURLパラメーターからアレルゲン・カテゴリの選択状態を復元
  const params = new URLSearchParams(window.location.search);
  const savedAllergens = params.get(PARAM_KEY);
  if (savedAllergens) {
    savedAllergens.split(",").forEach((a) => selectedAllergens.add(a));
  }
  const savedCategories = params.get(CATEGORY_PARAM_KEY);
  if (savedCategories) {
    savedCategories.split(",").forEach((c) => selectedCategories.add(c));
  }

  /**
   * DawnのAjax更新（フィルター操作・ソート変更）後にDOMが再レンダリングされるため、
   * ProductGridContainerの親要素をMutationObserverで監視し、
   * DOM更新のたびに各種同期処理を再実行する
   */
  const productGrid = document.getElementById("ProductGridContainer");
  if (productGrid) {
    const filterObserver = new MutationObserver(() => {
      syncCategoryActiveState();
      initLabels();
      initCategoryLabels();
      syncAllergenActiveState();
      filterProducts();
    });
    filterObserver.observe(productGrid.parentElement || productGrid, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * カテゴリpillのis-activeをselectedCategories（メモリ上の状態）から同期する
   * MutationObserver発火後（DOM再レンダリング後）に呼び出す
   *
   * 本番のカテゴリは custom.product_type metafield（主菜/副菜/おやつ・デザート）。
   * Search & Discoveryアプリに未登録のためShopifyデフォルトのフィルターは使えず、
   * アレルギーと同じ独自JS制御（selectedCategories + data-product-type）で動作する。
   */
  function syncCategoryActiveState() {
    document.querySelectorAll("[data-category-value]").forEach((label) => {
      const value = label.dataset.categoryValue;
      if (selectedCategories.has(value)) {
        label.classList.add("is-active");
      } else {
        label.classList.remove("is-active");
      }
    });
  }

  /**
   * アレルゲンpillのis-activeをselectedAllergens（メモリ上の状態）から同期する
   * MutationObserver発火後（DOM再レンダリング後）に呼び出す
   */
  function syncAllergenActiveState() {
    document.querySelectorAll("[data-allergen-value]").forEach((label) => {
      const value = label.dataset.allergenValue;
      if (selectedAllergens.has(value)) {
        label.classList.add("is-active");
      } else {
        label.classList.remove("is-active");
      }
    });
  }

  /**
   * モバイルの「クリア」ボタンクリック時の処理
   *
   * クリアボタンはページ遷移するURLリンクのため、遷移後にDOMContentLoadedが
   * 再実行されselectedAllergenは空で初期化される。
   * しかしページ遷移前にMutationObserverが発火しないため、is-activeとfilterProductsを
   * 手動で即座にリセットする必要がある。
   */
  document.addEventListener("click", (e) => {
    const clearLink = e.target.closest(
      ".mobile-facets__clear, .active-facets__button-remove",
    );
    if (!clearLink) return;

    selectedAllergens.clear();
    selectedCategories.clear();
    updateURL();
    updateCategoryURL();
    // ページ遷移前にis-activeを即座に削除
    document
      .querySelectorAll(
        "[data-allergen-value].is-active, [data-category-value].is-active",
      )
      .forEach((label) => {
        label.classList.remove("is-active");
      });
    filterProducts();
  });

  /**
   * アレルギー「すべて選択 / すべて解除」トグルボタン（No.7/54、2026-07-26）
   * - 未選択のとき押す → 全28品目を除外対象に（=アレルゲンを1つも含まない商品だけ表示）
   * - 1つでも選択中のとき押す → 選択を全解除（カテゴリの選択は保持）
   * 文言は選択状態に応じて updateBulkToggleLabel() が「すべて選択/すべて解除」を切り替える。
   * ボタンはdesktop/mobile両方に出るためイベント委任で拾う。
   */
  function updateBulkToggleLabel() {
    const text = selectedAllergens.size > 0 ? "すべて解除" : "すべて選択";
    document.querySelectorAll("[data-allergen-toggle-all]").forEach((btn) => {
      if (btn.textContent !== text) btn.textContent = text;
    });
  }

  document.addEventListener("click", (e) => {
    const toggleBtn = e.target.closest("[data-allergen-toggle-all]");
    if (!toggleBtn) return;
    e.preventDefault();

    if (selectedAllergens.size > 0) {
      selectedAllergens.clear();
    } else {
      document.querySelectorAll("[data-allergen-value]").forEach((label) => {
        selectedAllergens.add(label.dataset.allergenValue);
      });
    }
    syncAllergenActiveState();
    updateURL();
    filterProducts();
  });

  /**
   * DawnのfacetsはAjax後にhistory.pushStateでURLを更新する。
   * その際exclude_allergensパラメーターが消えてしまうため、
   * pushStateをフックして確実にパラメーターを維持する。
   */
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    ensureParam();
  };

  // ブラウザの戻る/進む操作時にも状態を同期する
  window.addEventListener("popstate", () => {
    ensureParam();
    syncCategoryActiveState();
    filterProducts();
  });

  /**
   * Ajax後のURL更新でexclude_allergens・categoryが消えた場合に再付与する
   * （DawnのfacetsがURLを上書きするタイミングで呼ばれる）
   */
  function ensureParam() {
    const url = new URL(window.location.href);
    let changed = false;
    if (selectedAllergens.size > 0 && !url.searchParams.has(PARAM_KEY)) {
      url.searchParams.set(PARAM_KEY, [...selectedAllergens].join(","));
      changed = true;
    }
    if (
      selectedCategories.size > 0 &&
      !url.searchParams.has(CATEGORY_PARAM_KEY)
    ) {
      url.searchParams.set(
        CATEGORY_PARAM_KEY,
        [...selectedCategories].join(","),
      );
      changed = true;
    }
    if (changed) {
      history.replaceState({}, "", url.toString());
    }
  }

  /**
   * アレルゲンpillにクリックイベントを登録する
   * WeakSetで初期化済みの要素をスキップし、DOM再レンダリング後の新しい要素のみ登録する
   *
   * アレルゲンpillのcheckboxはdisabled属性のためShopifyデフォルトのフィルターには
   * 影響せず、独自のJS制御（selectedAllergens + filterProducts）で動作する
   */
  function initLabels() {
    document.querySelectorAll("[data-allergen-value]").forEach((label) => {
      if (initializedLabels.has(label)) return;
      initializedLabels.add(label);

      const value = label.dataset.allergenValue;

      if (selectedAllergens.has(value)) {
        label.classList.add("is-active");
      }

      label.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const checkbox = label.querySelector('input[type="checkbox"]');

        if (selectedAllergens.has(value)) {
          selectedAllergens.delete(value);
          label.classList.remove("is-active");
          if (checkbox) checkbox.checked = false;
        } else {
          selectedAllergens.add(value);
          label.classList.add("is-active");
          if (checkbox) checkbox.checked = true;
        }

        updateURL();
        filterProducts();
      });
    });
  }

  /**
   * カテゴリpillにクリックイベントを登録する（initLabelsのカテゴリ版）
   * WeakSetで初期化済みの要素をスキップし、DOM再レンダリング後の新しい要素のみ登録する
   *
   * カテゴリpillのcheckboxはdisabled属性のためShopifyデフォルトのフィルターには
   * 影響せず、独自のJS制御（selectedCategories + filterProducts）で動作する
   */
  function initCategoryLabels() {
    document.querySelectorAll("[data-category-value]").forEach((label) => {
      if (initializedCategoryLabels.has(label)) return;
      initializedCategoryLabels.add(label);

      const value = label.dataset.categoryValue;

      if (selectedCategories.has(value)) {
        label.classList.add("is-active");
      }

      label.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const checkbox = label.querySelector('input[type="checkbox"]');

        if (selectedCategories.has(value)) {
          selectedCategories.delete(value);
          label.classList.remove("is-active");
          if (checkbox) checkbox.checked = false;
        } else {
          selectedCategories.add(value);
          label.classList.add("is-active");
          if (checkbox) checkbox.checked = true;
        }

        updateCategoryURL();
        filterProducts();
      });
    });
  }

  /**
   * selectedAllergenの状態をURLのexclude_allergensパラメーターに反映する
   * history.replaceStateを使用するためブラウザの履歴には残らない
   */
  function updateURL() {
    const url = new URL(window.location.href);
    if (selectedAllergens.size > 0) {
      url.searchParams.set(PARAM_KEY, [...selectedAllergens].join(","));
    } else {
      url.searchParams.delete(PARAM_KEY);
    }
    window.history.replaceState({}, "", url.toString());
  }

  /**
   * selectedCategoriesの状態をURLのcategoryパラメーターに反映する
   * history.replaceStateを使用するためブラウザの履歴には残らない
   */
  function updateCategoryURL() {
    const url = new URL(window.location.href);
    if (selectedCategories.size > 0) {
      url.searchParams.set(CATEGORY_PARAM_KEY, [...selectedCategories].join(","));
    } else {
      url.searchParams.delete(CATEGORY_PARAM_KEY);
    }
    window.history.replaceState({}, "", url.toString());
  }

  /**
   * selectedAllergen・selectedCategoriesに基づいて商品カードの表示/非表示を切り替える
   * - アレルギー: data-allergens属性に選択中のアレルゲンが含まれる場合は非表示（除外方式）
   * - カテゴリ: data-product-type属性が選択中のカテゴリのいずれかと一致する場合のみ表示（包含方式）
   *   data-product-type属性を持たないカード（choiceカード等、本フィルタ対象外のカード種別）は
   *   カテゴリ判定の対象外とし常に表示する
   * 両条件はAND（アレルギー除外を通過し、かつカテゴリ条件を満たす場合のみ表示）
   * ShopifyデフォルトのフィルターではなくCSS表示制御のため即座に反映される
   */
  function filterProducts() {
    document.querySelectorAll("[data-allergens]").forEach((card) => {
      let visible = true;

      if (selectedAllergens.size > 0) {
        const productAllergens = card.dataset.allergens
          ? card.dataset.allergens.split(",")
          : [];
        const hasExcluded = [...selectedAllergens].some((a) =>
          productAllergens.includes(a),
        );
        if (hasExcluded) visible = false;
      }

      if (
        visible &&
        selectedCategories.size > 0 &&
        card.dataset.productType !== undefined
      ) {
        if (!selectedCategories.has(card.dataset.productType)) {
          visible = false;
        }
      }

      card.style.display = visible ? "" : "none";
    });
    updateBulkToggleLabel();
  }

  // 初期化
  initLabels();
  initCategoryLabels();
  syncCategoryActiveState();
  filterProducts();
});
